import React from 'react';

interface CardProps {
  children: React.ReactNode;
  className?: string;
  hover?: boolean;
  glow?: boolean;
  onClick?: () => void;
  style?: React.CSSProperties;
}

export const Card: React.FC<CardProps> = ({
  children,
  className = '',
  hover = true,
  glow = false,
  onClick,
}) => {
  return (
    <div
      className={`
        bg-bg-tertiary rounded-2xl border border-border-subtle p-5 md:p-6
        transition-all duration-500 ease-smooth
        ${hover ? 'hover:-translate-y-1 hover:border-apple-blue/30 hover:shadow-[0_20px_40px_rgba(0,0,0,0.4)]' : ''}
        ${glow ? 'glow-subtle' : ''}
        ${onClick ? 'cursor-pointer' : ''}
        ${className}
      `}
      onClick={onClick}
    >
      {children}
    </div>
  );
};

interface ServiceCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  details?: string[];
  className?: string;
  delay?: number;
}

export const ServiceCard: React.FC<ServiceCardProps> = ({
  icon,
  title,
  description,
  details,
  className = '',
  delay = 0,
}) => {
  return (
    <Card 
      className={`group h-full ${className}`}
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex flex-col h-full">
        {/* Icon */}
        <div className="w-12 h-12 rounded-xl bg-apple-blue/10 flex items-center justify-center mb-5 group-hover:bg-apple-blue/20 transition-colors duration-300">
          <div className="text-apple-blue">
            {icon}
          </div>
        </div>
        
        {/* Title */}
        <h3 className="text-h4 font-semibold text-text-primary mb-3">
          {title}
        </h3>
        
        {/* Description */}
        <p className="text-body text-text-secondary mb-4 flex-grow">
          {description}
        </p>
        
        {/* Details */}
        {details && details.length > 0 && (
          <ul className="space-y-2 mt-auto">
            {details.map((detail, i) => (
              <li key={i} className="flex items-start gap-2 text-body-s text-text-tertiary">
                <span className="w-1 h-1 rounded-full bg-apple-blue mt-2 flex-shrink-0" />
                {detail}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
};

interface StatCardProps {
  value: string;
  label: string;
  suffix?: string;
  className?: string;
}

export const StatCard: React.FC<StatCardProps> = ({
  value,
  label,
  suffix,
  className = '',
}) => {
  return (
    <div className={`text-center ${className}`}>
      <div className="flex items-baseline justify-center gap-1">
        <span className="text-display-m font-bold gradient-text">{value}</span>
        {suffix && <span className="text-h3 text-apple-blue">{suffix}</span>}
      </div>
      <p className="text-body-s text-text-secondary mt-1">{label}</p>
    </div>
  );
};

export default Card;
